import type { Session, TenantBundle } from './types';
import type { AppState } from './store';
import { minutesOf } from './selectors';

/**
 * Behaviour-based suggestions.
 *
 * Deterministic and on-device, deliberately: no model call, no network, so it
 * renders instantly, works in a basement hall, and costs nothing per attendee.
 * It reads four signals — what they starred, what they opened, what they are
 * booking, and what they said they like — and every suggestion carries the
 * reason it was made, because "because you starred X" reads as help while an
 * unexplained recommendation reads as surveillance.
 */

export type Strategy = 'both' | 'behavior' | 'interests';

export interface Suggestion {
  session: Session;
  score: number;
  /** Plain-language reasons, strongest first. */
  reasons: string[];
}

interface Weighted { weight: number; text: string }

const overlaps = (a: Session, b: Session) =>
  a.day === b.day && minutesOf(a.t) < minutesOf(b.end) && minutesOf(a.end) > minutesOf(b.t);

export function suggestSessions(
  bundle: TenantBundle,
  state: Pick<AppState, 'saved' | 'viewed' | 'cart' | 'interests' | 'day' | 'journey'>,
  opts: { strategy?: Strategy; limit?: number; now?: Date } = {},
): Suggestion[] {
  const strategy = opts.strategy ?? 'both';
  const limit = opts.limit ?? 3;
  const useBehavior = strategy !== 'interests';
  const useInterests = strategy !== 'behavior';

  const nameOf = (id: string) => bundle.talent.find((t) => t.id === id)?.name ?? id;
  const byId = new Map(bundle.sessions.map((s) => [s.id, s]));

  const planned = new Set([...state.saved, ...Object.values(state.journey).flat()]);
  const savedSessions = [...planned].map((id) => byId.get(id)).filter((s): s is Session => !!s);

  // What the attendee's own choices say they like.
  const tagWeight = new Map<string, number>();
  const trackWeight = new Map<string, number>();
  const guestWeight = new Map<string, number>();
  for (const s of savedSessions) {
    for (const tag of s.tags ?? []) tagWeight.set(tag, (tagWeight.get(tag) ?? 0) + 1);
    trackWeight.set(s.track, (trackWeight.get(s.track) ?? 0) + 1);
    for (const g of s.who) guestWeight.set(g, (guestWeight.get(g) ?? 0) + 1);
  }

  const viewedTalent = new Set(state.viewed.talent);
  const viewedSessions = state.viewed.sessions.map((id) => byId.get(id)).filter((s): s is Session => !!s);
  const bookedTalent = new Set(
    state.cart.filter((l): l is Extract<typeof l, { kind: 'booking' }> => l.kind === 'booking').map((l) => l.talentId),
  );

  const now = opts.now ?? new Date();
  const dayInfo = bundle.days.find((d) => d.key === state.day);
  // Only prune the past when the selected day is genuinely today; otherwise the
  // whole day is still ahead of the attendee.
  const isToday = dayInfo ? dayInfo.date === now.toISOString().slice(0, 10) : false;
  const nowMins = now.getHours() * 60 + now.getMinutes();

  const out: Suggestion[] = [];

  for (const s of bundle.sessions) {
    if (s.day !== state.day) continue;
    if (planned.has(s.id)) continue;
    if (isToday && minutesOf(s.end) <= nowMins) continue;
    // Suggesting something that clashes with what they already chose is worse
    // than suggesting nothing.
    if (useBehavior && savedSessions.some((x) => overlaps(s, x))) continue;

    const reasons: Weighted[] = [];

    if (useInterests) {
      const hits = (s.tags ?? []).filter((tag) => state.interests.includes(tag));
      if (hits.length) reasons.push({ weight: 2 * hits.length, text: `Matches ${hits.slice(0, 2).join(' and ')}` });
    }

    if (useBehavior) {
      for (const g of s.who) {
        if (bookedTalent.has(g)) reasons.push({ weight: 3.2, text: `You\u2019re booking ${nameOf(g)}` });
        else if (guestWeight.has(g)) reasons.push({ weight: 2.4, text: `${nameOf(g)} is in your plan` });
        else if (viewedTalent.has(g)) reasons.push({ weight: 2.2, text: `You looked at ${nameOf(g)}` });
      }
      const shared = (s.tags ?? []).filter((tag) => tagWeight.has(tag));
      if (shared.length) {
        reasons.push({ weight: 1.5 * shared.length, text: `Like what you starred \u2014 ${shared[0]}` });
      }
      if (trackWeight.has(s.track)) reasons.push({ weight: 1, text: `You\u2019ve starred other ${s.track.toLowerCase()} sessions` });
      const seenLike = viewedSessions.find((v) => v.track === s.track && v.id !== s.id);
      if (seenLike) reasons.push({ weight: 0.6, text: `Similar to ${seenLike.title}` });
      if (savedSessions.length && reasons.length) reasons.push({ weight: 0.4, text: 'Fits around your plan' });
    }

    if (!reasons.length) continue;
    reasons.sort((a, b) => b.weight - a.weight);
    out.push({
      session: s,
      score: reasons.reduce((n, r) => n + r.weight, 0),
      reasons: reasons.slice(0, 2).map((r) => r.text),
    });
  }

  out.sort((a, b) => b.score - a.score || minutesOf(a.session.t) - minutesOf(b.session.t));

  // Cold start: nothing known about this attendee yet. Say so honestly rather
  // than inventing a reason — offer what is soonest.
  if (out.length === 0) {
    return bundle.sessions
      .filter((s) => s.day === state.day && !planned.has(s.id) && !(isToday && minutesOf(s.end) <= nowMins))
      .sort((a, b) => minutesOf(a.t) - minutesOf(b.t))
      .slice(0, limit)
      .map((session) => ({ session, score: 0, reasons: [`Starts at ${session.t}`] }));
  }
  return out.slice(0, limit);
}
